import React, { useState, useEffect, useRef } from 'react';
import { db } from '../db';
import { toast, confirmDialog } from '../utils/feedback';
import { getEarnedBadges } from '../utils/badgeUtils';
import { LineChart, Line, BarChart, Bar, Legend, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Trash2, Upload, Image as ImageIcon, X, User, Ruler, Activity } from 'lucide-react';

const CustomXAxisTick = ({ x, y, payload }) => {
  if (payload && payload.value) {
    const text = payload.value;
    const maxLineLength = 20;
    const words = text.split(' ');
    let lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      if (currentLine.length + words[i].length + 1 <= maxLineLength) {
        currentLine += ' ' + words[i];
      } else {
        lines.push(currentLine);
        currentLine = words[i];
      }
    }
    lines.push(currentLine);

    return (
      <g transform={`translate(${x},${y + 15})`}>
        <text x={0} y={0} dy={4} textAnchor="end" fill="var(--text-secondary)" fontSize={12} transform="rotate(-30)">
          {lines.map((line, index) => (
            <tspan x={0} dy={index === 0 ? 0 : 14} key={index}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  }
  return null;
};

const BodyTracker = ({ currentProfile }) => {
  const [measurements, setMeasurements] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  
  const [weightValue, setWeightValue] = useState('');
  const [waistValue, setWaistValue] = useState('');
  const [armsValue, setArmsValue] = useState('');
  const [thighsValue, setThighsValue] = useState('');
  const [boneMassValue, setBoneMassValue] = useState('');
  const [muscleMassValue, setMuscleMassValue] = useState('');
  const [bodyFatValue, setBodyFatValue] = useState('');
  
  const [selectedChartMetric, setSelectedChartMetric] = useState('weight');
  
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isGalleryExpanded, setIsGalleryExpanded] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (currentProfile) loadMeasurements();
  }, [currentProfile?.id]);

  const loadMeasurements = async () => {
    setLoading(true);
    // Fetch weights, exercises, and workouts simultaneously
    const [weightData, eData, wData] = await Promise.all([
      db.getWeights(currentProfile.id),
      db.getExercises(currentProfile.id),
      db.getWorkouts(currentProfile.id)
    ]);
    
    setMeasurements(weightData);

    const prs = {};
    const rms = {};
    const fallbackNames = {};

    wData.forEach(workout => {
      workout.exercises.forEach(block => {
        const items = block.type === 'superset' ? block.items : [block];
        items.forEach(ex => {
          if (ex.name) fallbackNames[ex.exerciseId] = ex.name;
          if (ex.sets) {
            ex.sets.forEach(set => {
              // Unilateral has weightL, weightR. Normal has weight. We take max weight if unilateral.
              const weight = set.isUnilateral ? Math.max(parseFloat(set.weightL) || 0, parseFloat(set.weightR) || 0) : parseFloat(set.weight);
              const reps = set.isUnilateral ? Math.max(parseInt(set.repsL) || 0, parseInt(set.repsR) || 0) : parseInt(set.reps);
              
              if (weight > 0 && reps > 0) {
                if (!prs[ex.exerciseId] || weight > prs[ex.exerciseId]) {
                  prs[ex.exerciseId] = weight;
                }
                const current1RM = weight * (1 + reps / 30);
                if (!rms[ex.exerciseId] || current1RM > rms[ex.exerciseId]) {
                  rms[ex.exerciseId] = Math.round(current1RM);
                }
              }
            });
          }
        });
      });
    });

    const perfData = Object.keys(prs).map(exId => {
      const exercise = eData.find(e => String(e.id) === String(exId));
      return {
        name: exercise ? exercise.name : (fallbackNames[exId] || 'Inconnu'),
        pr: prs[exId],
        est1RM: rms[exId]
      };
    });

    // Sort by heaviest PR first
    perfData.sort((a,b) => b.pr - a.pr);
    
    setPerformanceData(perfData);
    setLoading(false);
  };

  const resizeImage = (file, maxWidth, maxHeight) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compress quality to save space
          resolve(canvas.toDataURL('image/jpeg', 0.6));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setPhotoFile(file);
      const base64 = await resizeImage(file, 800, 800);
      setPhotoPreview(base64);
    }
  };

  const handleAddMeasurement = async (e) => {
    e.preventDefault();
    if (!weightValue && !waistValue && !armsValue && !thighsValue && !boneMassValue && !muscleMassValue && !bodyFatValue && !photoFile) return;

    const valuesToCheck = [weightValue, waistValue, armsValue, thighsValue, boneMassValue, muscleMassValue, bodyFatValue];
    if (valuesToCheck.some(val => val !== '' && parseFloat(val) < 0)) {
      toast.error("Les valeurs ne peuvent pas être négatives.");
      return;
    }

    setSaving(true);

    let photoUrl = '';
    if (photoPreview) {
      photoUrl = photoPreview; 
    }

    const w = parseFloat(weightValue) || null;
    
    await db.addWeight({
      profileId: currentProfile.id,
      date,
      weight: w,
      waist: parseFloat(waistValue) || null,
      arms: parseFloat(armsValue) || null,
      thighs: parseFloat(thighsValue) || null,
      boneMass: parseFloat(boneMassValue) || null,
      muscleMass: parseFloat(muscleMassValue) || null,
      bodyFat: parseFloat(bodyFatValue) || null,
      photoUrl
    });
    
    await db.addXP(currentProfile.id, 20, 'body');

    if (w) {
      const activeGoals = await db.getGoals(currentProfile.id);
      const workouts = await db.getWorkouts(currentProfile.id);
      const badgesBefore = getEarnedBadges(workouts, activeGoals, currentProfile);
      
      let goalsUpdated = false;
      for (const goal of activeGoals) {
        if (!goal.completed && goal.linkedToWeight && (goal.type === 'Perte de Poids' || goal.type === 'Prise de Masse / Poids')) {
          let isCompleted = false;
          if (goal.type === 'Perte de Poids') isCompleted = w <= goal.targetValue && w > 0;
          if (goal.type === 'Prise de Masse / Poids') isCompleted = w >= goal.targetValue;
          
          if (w !== goal.currentValue) {
             await db.updateGoal({ ...goal, currentValue: w, completed: isCompleted });
             goalsUpdated = true;
          }
        }
      }
      
      if (goalsUpdated) {
        const updatedGoals = await db.getGoals(currentProfile.id);
        const badgesAfter = getEarnedBadges(workouts, updatedGoals, currentProfile);
        const alreadyAwardedIds = new Set([
          ...badgesBefore.map(b => b.id),
          ...workouts.flatMap(w => (w.unlockedBadges || []).map(b => (typeof b === 'object' ? b.id : b)).filter(Boolean)),
          ...(Array.isArray(currentProfile?.badges) ? currentProfile.badges : [])
        ]);
        const newBadges = badgesAfter.filter(b => !alreadyAwardedIds.has(b.id));
        if (newBadges.length > 0) {
          const xpToAward = newBadges.length * 35;
          await db.addBadgesToProfile(currentProfile.id, newBadges.map(b => b.id));
          await db.addXP(currentProfile.id, xpToAward);
          toast.success(`Succès débloqué ! +${xpToAward} XP`);
        }
      }
    }
    
    setWeightValue('');
    setWaistValue('');
    setArmsValue('');
    setThighsValue('');
    setBoneMassValue('');
    setMuscleMassValue('');
    setBodyFatValue('');
    setPhotoFile(null);
    setPhotoPreview('');
    setSaving(false);
    loadMeasurements();
  };

  const handleDelete = async (id) => {
    confirmDialog('Veux-tu vraiment supprimer cette mesure ?', async () => {
      await db.deleteWeight(id);
      loadMeasurements();
      toast.success("Mesure supprimée avec succès.");
    });
  };

  if (loading) return <div className="text-center mt-4">Chargement...</div>;

  // Format data for Recharts (we'll display weight by default)
  const metricLabels = {
    weight: 'Poids (kg)',
    waist: 'Taille (cm)',
    arms: 'Bras (cm)',
    thighs: 'Cuisses (cm)',
    bodyFat: 'Masse Graisseuse (%)',
    muscleMass: 'Masse Musculaire (%)',
    boneMass: 'Masse Osseuse (%)'
  };

  const chartData = measurements.filter(m => m[selectedChartMetric] != null).map(m => ({
    date: new Date(m.date).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' }),
    value: m[selectedChartMetric]
  })).slice(-30);

  let minVal = 0;
  let maxVal = 100;
  if (chartData.length > 0) {
    const values = chartData.map(w => w.value);
    const padding = selectedChartMetric === 'weight' ? 2 : (selectedChartMetric.includes('Mass') || selectedChartMetric === 'bodyFat' ? 1 : 2);
    minVal = Math.floor(Math.min(...values)) - padding;
    maxVal = Math.ceil(Math.max(...values)) + padding;
  }

  // Filter photos
  const photos = measurements.filter(m => m.photoUrl).sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div>
      <h2>Évolution Physique</h2>
      
      <div className="card mb-3">
        <h3>Nouvelles Mensurations</h3>
        <form onSubmit={handleAddMeasurement} style={{ marginTop: '1rem' }}>
          
          {/* Section: Général */}
          <h4 style={{ marginBottom: '0.8rem', color: 'var(--text-primary)', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <User size={18} color="var(--primary-color)" /> Général
          </h4>
          <div className="body-tracker-grid-2">
            <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
              <label className="form-label">Date</label>
              <input 
                type="date" 
                className="form-control" 
                value={date} 
                onChange={(e) => setDate(e.target.value)} 
                required 
                style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
              <label className="form-label">Poids (kg)</label>
              <input 
                type="number" 
                step="0.1" 
                min="0" 
                className="form-control" 
                placeholder="Ex: 75.5" 
                value={weightValue} 
                onChange={(e) => setWeightValue(e.target.value)} 
                style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Section: Mensurations */}
          <h4 style={{ marginBottom: '0.8rem', color: 'var(--text-primary)', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Ruler size={18} color="var(--secondary-color)" /> Mensurations (cm)
          </h4>
          <div className="body-tracker-grid-3">
            <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
              <label className="form-label" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Taille</label>
              <input 
                type="number" 
                step="0.5" 
                min="0" 
                className="form-control" 
                placeholder="Ex: 80" 
                value={waistValue} 
                onChange={(e) => setWaistValue(e.target.value)} 
                style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
              <label className="form-label" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Bras</label>
              <input 
                type="number" 
                step="0.5" 
                min="0" 
                className="form-control" 
                placeholder="Ex: 38" 
                value={armsValue} 
                onChange={(e) => setArmsValue(e.target.value)} 
                style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
              <label className="form-label" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Cuisses</label>
              <input 
                type="number" 
                step="0.5" 
                min="0" 
                className="form-control" 
                placeholder="Ex: 58" 
                value={thighsValue} 
                onChange={(e) => setThighsValue(e.target.value)} 
                style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Section: Composition corporelle */}
          <h4 style={{ marginBottom: '0.8rem', color: 'var(--text-primary)', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Activity size={18} color="#e74c3c" /> Composition corporelle (%)
          </h4>
          <div className="body-tracker-grid-3">
            <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
              <label className="form-label" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Masse Grasse</label>
              <input 
                type="number" 
                step="0.1" 
                min="0" 
                className="form-control" 
                placeholder="Ex: 15.5" 
                value={bodyFatValue} 
                onChange={(e) => setBodyFatValue(e.target.value)} 
                style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
              <label className="form-label" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Muscle</label>
              <input 
                type="number" 
                step="0.1" 
                min="0" 
                className="form-control" 
                placeholder="Ex: 40.2" 
                value={muscleMassValue} 
                onChange={(e) => setMuscleMassValue(e.target.value)} 
                style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0, minWidth: 0 }}>
              <label className="form-label" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Os</label>
              <input 
                type="number" 
                step="0.1" 
                min="0" 
                className="form-control" 
                placeholder="Ex: 4.5" 
                value={boneMassValue} 
                onChange={(e) => setBoneMassValue(e.target.value)} 
                style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Photo d'évolution (Avant / Après)</label>
            <div 
              style={{ width: '100px', height: '100px', borderRadius: 'var(--border-radius-sm)', backgroundColor: 'var(--bg-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', border: '1px dashed var(--border-color)', position: 'relative' }}
              onClick={() => fileInputRef.current?.click()}
            >
              {photoPreview ? (
                <img loading="lazy" src={photoPreview} alt="Aperçu" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Upload size={24} color="var(--text-secondary)" />
              )}
            </div>
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleFileChange} />
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </form>
      </div>

      {photos.length > 0 && (
        <div className="card mb-3">
          <h3>Galerie Avant / Après</h3>
          <div style={{ position: 'relative' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, 150px)', 
              justifyContent: 'center',
              gap: '1rem', 
              padding: '1rem 0',
              maxHeight: isGalleryExpanded ? '5000px' : '260px',
              overflow: 'hidden',
              transition: 'max-height 0.4s ease-in-out'
            }}>
              {photos.map(p => (
                <div key={p.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                  <div 
                    style={{ width: '150px', height: '200px', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border-color)', cursor: 'pointer', flexShrink: 0 }}
                    onClick={() => setSelectedImage(p.photoUrl)}
                  >
                    <img loading="lazy" src={p.photoUrl} alt="Evolution" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    {new Date(p.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                    {p.weight && ` • ${p.weight}kg`}
                  </span>
                </div>
              ))}
            </div>
            {/* Show expand button if there are many photos (rough estimate: more than 4-5 photos) */}
            {photos.length > 4 && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: '0.5rem',
                paddingBottom: '0.5rem'
              }}>
                <button 
                  className="btn btn-outline" 
                  style={{ borderRadius: '20px', padding: '0.4rem 1rem', fontSize: '0.9rem', backgroundColor: 'var(--bg-color)', border: '1px solid var(--border-color)' }}
                  onClick={() => setIsGalleryExpanded(!isGalleryExpanded)}
                >
                  {isGalleryExpanded ? '▲ Réduire' : '▼ Voir tout'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0 }}>Évolution</h3>
          <select 
            value={selectedChartMetric} 
            onChange={(e) => setSelectedChartMetric(e.target.value)}
            className="form-control"
            style={{ width: 'auto', padding: '0.4rem 1rem', display: 'inline-block' }}
          >
            {Object.entries(metricLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        {chartData.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>Aucune donnée enregistrée pour cette métrique.</p>
        ) : (
          <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ height: '300px', width: chartData.length > 10 ? `${chartData.length * 40}px` : '100%', minWidth: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 15, right: 15, bottom: 20, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    stroke="var(--text-secondary)" 
                    tick={{ fontSize: 11 }}
                    tickMargin={10}
                    padding={{ left: 15, right: 15 }}
                  />
                  <YAxis 
                    domain={[minVal, maxVal]} 
                    stroke="var(--text-secondary)" 
                    tick={{ fontSize: 11 }}
                    tickMargin={6}
                    width={38}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--bg-card)', 
                      borderColor: 'var(--border-color)', 
                      color: 'var(--text-primary)',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    name={metricLabels[selectedChartMetric]} 
                    stroke="var(--secondary-color)" 
                    strokeWidth={3} 
                    dot={{ r: 5, fill: 'var(--secondary-color)', stroke: 'var(--bg-card)', strokeWidth: 2 }} 
                    activeDot={{ r: 7, stroke: 'var(--secondary-color)', strokeWidth: 2 }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {chartData.length === 1 && (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', marginTop: '0.8rem', fontStyle: 'italic' }}>
            💡 Une seule mesure enregistrée pour le moment. La courbe se reliera automatiquement dès ta deuxième pesée !
          </p>
        )}
      </div>

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h3>Performances Globales</h3>
        
        {performanceData.length === 0 ? (
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Aucune donnée d'entraînement enregistrée.</p>
        ) : (
          <div style={{ width: '100%', overflowX: 'auto', marginTop: '1.5rem' }}>
            {/* dynamic width to allow horizontal scrolling if there are many exercises */}
            <div style={{ height: '350px', width: `${Math.max(100, performanceData.length * 15)}%`, minWidth: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceData} margin={{ top: 40, right: 30, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                  <XAxis dataKey="name" stroke="var(--text-secondary)" tick={<CustomXAxisTick />} interval={0} height={100} />
                  <YAxis stroke="var(--text-secondary)" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)', borderRadius: 'var(--border-radius-sm)' }}
                    itemStyle={{ fontWeight: 'bold' }}
                    cursor={{fill: 'rgba(255,255,255,0.05)'}}
                  />
                  <Legend verticalAlign="top" align="left" wrapperStyle={{ top: 0, paddingBottom: '20px' }} />
                  <Bar dataKey="pr" name="PR Actuel (kg)" fill="var(--secondary-color)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="est1RM" name="1RM Estimé (kg)" fill="var(--primary-color)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
      
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h3>Historique Complet</h3>
        {measurements.length === 0 ? (
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Aucun historique.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
            {measurements.slice().reverse().map(m => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8rem', backgroundColor: 'var(--bg-color)', borderRadius: 'var(--border-radius-sm)' }}>
                <div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.2rem' }}>
                    {new Date(m.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  <div className="measurement-stats">
                    <div className="stats-group">
                      {m.weight && <span><strong style={{color: 'var(--secondary-color)'}}>{m.weight}kg</strong></span>}
                      {m.waist && <span>Taille: <strong>{m.waist}cm</strong></span>}
                      {m.arms && <span>Bras: <strong>{m.arms}cm</strong></span>}
                      {m.thighs && <span>Cuisses: <strong>{m.thighs}cm</strong></span>}
                    </div>
                    <div className="stats-group">
                      {m.muscleMass && <span>Muscle: <strong>{m.muscleMass}%</strong></span>}
                      {m.boneMass && <span>Os: <strong>{m.boneMass}%</strong></span>}
                      {m.bodyFat && <span>Gras: <strong>{m.bodyFat}%</strong></span>}
                      {m.photoUrl && (
                        <button 
                          className="btn-icon" 
                          style={{ padding: 0, margin: 0, display: 'inline-flex' }}
                          onClick={() => setSelectedImage(m.photoUrl)}
                          title="Voir la photo"
                        >
                          <ImageIcon size={18} color="var(--primary-color)" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <button className="btn-icon" onClick={() => handleDelete(m.id)} style={{ color: 'var(--danger-color)', padding: '0.4rem' }}>
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedImage && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.92)', 
            zIndex: 999999, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: 'calc(env(safe-area-inset-top, 0px) + 20px) 16px calc(env(safe-area-inset-bottom, 0px) + 20px) 16px',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            touchAction: 'none'
          }}
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="btn-icon" 
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImage(null);
            }} 
            style={{ 
              position: 'absolute', 
              top: 'max(16px, calc(env(safe-area-inset-top, 0px) + 12px))', 
              right: 'max(16px, calc(env(safe-area-inset-right, 0px) + 12px))', 
              backgroundColor: 'rgba(30, 30, 30, 0.75)', 
              borderRadius: '50%', 
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              cursor: 'pointer',
              zIndex: 1000000
            }}
          >
            <X size={24} color="#fff" />
          </button>
          <img loading="lazy" 
            src={selectedImage} 
            alt="Agrandissement" 
            style={{ 
              maxWidth: 'min(92vw, 600px)', 
              maxHeight: 'calc(85dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))', 
              objectFit: 'contain', 
              borderRadius: '12px', 
              boxShadow: '0 12px 35px rgba(0,0,0,0.6)' 
            }} 
            onClick={e => e.stopPropagation()} 
          />
        </div>
      )}
    </div>
  );
};

export default BodyTracker;
